import { useMemo, useState, useEffect, useContext, useCallback, createContext } from 'react';

import { useVirtualLogin } from 'src/contexts/virtual-login-context';
import { useGetActiveCompetitions, useUpdateSelectedCompetitionId } from 'src/api/services/zerreactor_backend_definition/zerreactorBackendService';

import { useAuthContext } from 'src/auth/hooks/use-auth-context';

// Constants
const SELECTED_COMPETITION_KEY = 'selectedCompetition';

// Create context
const ActiveCompetitionContext = createContext({});

// Provider component
export function ActiveCompetitionProvider({ children }) {
  const [selectedCompetition, setSelectedCompetition] = useState(null);
  const [activeCompetitions, setActiveCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Get authentication status and user data
  const { authenticated, loading: authLoading, user } = useAuthContext();
  
  // Get virtual login context (useVirtualLogin handles its own fallback)
  const virtualLoginContext = useVirtualLogin();
  
  // Get update function for selected competition
  const { updateSelectedCompetitionId } = useUpdateSelectedCompetitionId();

  // SWR hook for fetching active competitions - only fetch when authenticated
  const { 
    activeCompetitions: fetchedActiveCompetitions, 
    activeCompetitionsLoading, 
    activeCompetitionsError 
  } = useGetActiveCompetitions(authenticated && !authLoading);

  // Load selected competition from localStorage on mount
  const loadSelectedCompetitionFromStorage = useCallback(() => {
    try {
      const storedCompetition = localStorage.getItem(SELECTED_COMPETITION_KEY);
      if (storedCompetition) {
        const parsedCompetition = JSON.parse(storedCompetition);
        setSelectedCompetition(parsedCompetition);
        setLoading(false);
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Error loading selected competition from storage:', error);
      setSelectedCompetition(null);
      setLoading(false);
    }
  }, []);

  // Save selected competition to localStorage
  const saveSelectedCompetitionToStorage = useCallback((competition) => {
    try {
      if (competition) {
        localStorage.setItem(SELECTED_COMPETITION_KEY, JSON.stringify(competition));
      } else {
        localStorage.removeItem(SELECTED_COMPETITION_KEY);
      }
    } catch (error) {
      console.error('Error saving selected competition to storage:', error);
    }
  }, []);

  // Update active competitions and selected competition when fetched from API
  useEffect(() => {
    if (!authenticated || authLoading) {
      // If not authenticated, clear everything and stop loading
      setSelectedCompetition(null);
      setActiveCompetitions([]);
      setLoading(false);
      localStorage.removeItem(SELECTED_COMPETITION_KEY);
      return;
    }

    if (fetchedActiveCompetitions && !activeCompetitionsLoading && !activeCompetitionsError) {
      // Update active competitions list
      setActiveCompetitions(fetchedActiveCompetitions);
      setLoading(false);
      
      // Find the selected competition from the fetched active competitions using user's SelectedCompetitionId
      if (user?.SelectedCompetitionId && fetchedActiveCompetitions.length > 0) {
        const selectedComp = fetchedActiveCompetitions.find(comp => comp.Id === user.SelectedCompetitionId);
        if (selectedComp) {
          setSelectedCompetition(selectedComp);
          saveSelectedCompetitionToStorage(selectedComp);
        }
      }
    } else if (!activeCompetitionsLoading && activeCompetitionsError) {
      // On error, keep the stored competition if available, but stop loading
      setLoading(false);
    }
  }, [
    authenticated, 
    authLoading, 
    fetchedActiveCompetitions, 
    activeCompetitionsLoading, 
    activeCompetitionsError, 
    user?.SelectedCompetitionId,
    user?.FirstName,
    user?.Id,
    saveSelectedCompetitionToStorage
  ]);

  // Load from storage on mount - only if authenticated
  useEffect(() => {
    if (authenticated && !authLoading) {
      loadSelectedCompetitionFromStorage();
    } else if (!authLoading) {
      // Clear if not authenticated
      setSelectedCompetition(null);
      setActiveCompetitions([]);
      setLoading(false);
      localStorage.removeItem(SELECTED_COMPETITION_KEY);
    }
  }, [authenticated, authLoading, loadSelectedCompetitionFromStorage]);

  // Cross-tab synchronization
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === SELECTED_COMPETITION_KEY) {
        if (event.newValue) {
          try {
            const newCompetition = JSON.parse(event.newValue);
                      setSelectedCompetition(newCompetition);
          setLoading(false);
          } catch (error) {
            console.error('Error parsing selected competition from storage event:', error);
          }
        } else {
          setSelectedCompetition(null);
          setLoading(false);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Refresh active competitions manually
  const refreshActiveCompetitions = useCallback(async () => {
    // This will be handled by SWR revalidation
    // The SWR hook will automatically refetch when called
    setLoading(true);
  }, []);

  // Update selected competition
  const updateSelectedCompetition = useCallback(async (competitionId) => {
    try {
      setLoading(true);
      
      // Update on server
      await updateSelectedCompetitionId(competitionId);
      
      // Find the competition from active competitions
      const selectedComp = activeCompetitions.find(comp => comp.Id === competitionId);
      
      if (selectedComp) {
        setSelectedCompetition(selectedComp);
        saveSelectedCompetitionToStorage(selectedComp);
        
        // CRITICAL FIX: Update virtual user's localStorage data if in virtual login mode
        const currentUserData = localStorage.getItem('userData');
        if (currentUserData) {
          try {
            const userData = JSON.parse(currentUserData);
            if (userData && userData.Id) {
              userData.SelectedCompetitionId = competitionId;
              localStorage.setItem('userData', JSON.stringify(userData));
              
              // Also update virtual login context if available
              if (virtualLoginContext && virtualLoginContext.isVirtualLogin && virtualLoginContext.updateVirtualUserData) {
                virtualLoginContext.updateVirtualUserData({ SelectedCompetitionId: competitionId });
              }
            }
          } catch (parseError) {
            console.error('Error updating virtual user localStorage data:', parseError);
          }
        }
        
        setLoading(false);
      }
    } catch (error) {
      console.error('Error updating selected competition:', error);
      setLoading(false);
      throw error;
    }
  }, [updateSelectedCompetitionId, activeCompetitions, saveSelectedCompetitionToStorage, virtualLoginContext]);

  // Clear selected competition
  const clearSelectedCompetition = useCallback(() => {
    setSelectedCompetition(null);
    setLoading(false);
    localStorage.removeItem(SELECTED_COMPETITION_KEY);
  }, []);

  const memoizedValue = useMemo(
    () => {
      const contextValue = {
        selectedCompetition,
        activeCompetitions,
        loading: loading || activeCompetitionsLoading,
        error: activeCompetitionsError,
        refreshActiveCompetitions,
        updateSelectedCompetition,
        clearSelectedCompetition,
        hasSelectedCompetition: !!selectedCompetition,
        hasActiveCompetitions: activeCompetitions.length > 0,
        // Legacy support - keep old property names for backward compatibility temporarily
        activeCompetition: selectedCompetition,
        hasActiveCompetition: !!selectedCompetition,
      };
      
      return contextValue;
    },
    [
      selectedCompetition,
      activeCompetitions,
      loading, 
      activeCompetitionsLoading, 
      activeCompetitionsError, 
      refreshActiveCompetitions,
      updateSelectedCompetition,
      clearSelectedCompetition
    ]
  );

  return (
    <ActiveCompetitionContext.Provider value={memoizedValue}>
      {children}
    </ActiveCompetitionContext.Provider>
  );
}

// Hook to use active competition context
export function useActiveCompetition() {
  const context = useContext(ActiveCompetitionContext);
  
  if (!context) {
    throw new Error('useActiveCompetition must be used within ActiveCompetitionProvider');
  }
  
  return context;
}
